import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { HiChevronDown, HiMenu, HiRefresh } from 'react-icons/hi';
import { UserContext } from "../context/UserContext";
import Button, { TAMANO_ICONO } from './Elements/Button';
import classnames from 'classnames';
import notFoundImage from '../assets/fox-not-found.webp';
import { getImageUrl } from '../lib/helpers';
import { getVenueBrandName } from '../lib/consts/venueTypes';
import { cerrarSesion } from "../lib/authSession";

function VenueSelectTablet({ venues, selectedVenueId, onVenuePick }) {
  const selectedVenue = venues.find((v) => String(v.id) === String(selectedVenueId));
  const buttonLabel = selectedVenue?.name || 'Seleccionar tienda...';

  return (
    <div className="wa-venue-listbox relative w-full sm:w-auto min-w-0">
      <Listbox
        value={selectedVenueId ? String(selectedVenueId) : null}
        onChange={(venueId) => {
          if (!venueId) return;
          const venue = venues.find((v) => String(v.id) === String(venueId));
          if (venue && String(venue.id) !== String(selectedVenueId)) {
            onVenuePick(venue);
          }
        }}
      >
        <ListboxButton className="w-full sm:w-auto px-3 py-1 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2">
          <span className="whitespace-nowrap">{buttonLabel}</span>
          <HiChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          className="z-50 mt-1 max-h-60 min-w-[var(--button-width)] overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm text-left shadow-lg focus:outline-none"
        >
          <div className="px-3 py-1.5 text-gray-400 select-none" aria-hidden>
            Seleccionar tienda...
          </div>
          {venues.map((venue) => (
            <ListboxOption
              key={venue.id}
              value={String(venue.id)}
              className={({ focus, selected }) =>
                classnames(
                  'cursor-pointer px-3 py-1.5',
                  focus && 'bg-blue-50',
                  selected && 'font-medium text-blue-700'
                )
              }
            >
              {venue.name}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}

export default function HeaderNew({ onMenuClick, hayVersionNueva = false, onRecargar = () => window.location.reload() }) {
  // Al primer toque del aviso: etiqueta «Recargando…» y botón inerte. En la tablet con mala
  // wifi la recarga tarda segundos y sin esto se toca tres veces sin saber si ha hecho algo.
  const [recargando, setRecargando] = useState(false);
  const navigate = useNavigate();
  const [, setToken, ownedShops, venueID, venueName, venueImage, email, accessPrivileges, allVenues, changeVenue, , venueType] = useContext(UserContext);

  const handleLogout = () => {
    cerrarSesion();
    setToken(null);
  };

  const isSuperuser = accessPrivileges === 'superadmin';
  const isStaff = accessPrivileges === 'staff';
  const venueList = isSuperuser ? allVenues : ownedShops;

  const selectedVenue = venueID && venueList
    ? venueList.find(v => {
      const vId = String(v.id);
      const selectedId = String(venueID);
      return vId === selectedId || vId.toLowerCase() === selectedId.toLowerCase();
    })
    : null;

  const shopName = selectedVenue?.name
    || venueName
    || (isSuperuser ? (allVenues?.[0]?.name || "No shop selected") : (ownedShops?.[0]?.name ?? "No shop found"));
  const appName = getVenueBrandName(venueType);

  useEffect(() => {
    document.title = shopName ? `${appName} · ${shopName}` : appName;
  }, [appName, shopName]);

  const venueImagePath = isStaff ? venueImage : (selectedVenue?.image_path || venueImage);
  const avatarUrl = getImageUrl(venueImagePath, notFoundImage);

  // ÚNICA puerta a changeVenue: son 7 argumentos posicionales y los tres sitios
  // que la llamaban olvidaban el último (trial_ends_at), así que al cambiar de
  // tienda se perdía la fecha de fin de prueba y el bloqueo decía "nunca activada"
  // en vez de "prueba vencida".
  const pickVenue = (venue) => {
    if (!venue || !changeVenue) return;
    changeVenue(
      venue.id,
      venue.name,
      venue.image_path || '',
      venue.venue_type,
      venue.activation_status,
      venue.subscription_status,
      venue.trial_ends_at,
      venue.orders_enabled,
      venue.smack_enabled
    );
  };

  const handleVenueChange = (e) => {
    const selectedVenueId = e.target.value;
    const venue = allVenues.find(v => String(v.id) === selectedVenueId);
    pickVenue(venue);
  };

  const roleLabel = (() => {
    const raw = (accessPrivileges || '').toString().toLowerCase();
    const norm = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .replace(/[^a-z0-9]+/g, '');
    if (norm.includes('superadmin') || norm.includes('superadministr')) return 'Super Admin';
    if (norm === 'owner' || norm === 'propietario') return 'Owner';
    if (norm === 'staff' || norm === 'personal') return 'Staff';
    return accessPrivileges;
  })();

  const nativeSelectClassName =
    'wa-venue-native-select hidden lg:block px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto';

  return (
    <header className="bg-white h-14 sm:h-16 max-lg:portrait:min-h-14 max-lg:portrait:h-auto max-lg:portrait:py-2 px-3 sm:px-4 flex items-center max-lg:portrait:items-start border-b border-gray-200 min-w-0 max-lg:portrait:gap-2">
      <button
        className="md:hidden p-2 mr-4 max-lg:portrait:-ml-1 max-lg:portrait:mr-0 max-lg:portrait:shrink-0 text-gray-600"
        onClick={onMenuClick}
        aria-label="Open sidebar"
      >
        <HiMenu size={24} />
      </button>

      <div className="wa-header-venuebar flex-1 min-w-0 text-left md:text-right flex flex-col sm:flex-row max-lg:portrait:flex-col items-start sm:items-center max-lg:portrait:items-start gap-2 sm:gap-4 max-lg:portrait:gap-1.5">
        {isSuperuser && allVenues && allVenues.length > 0 && (
          <>
            <select
              value={venueID || ''}
              onChange={handleVenueChange}
              className={nativeSelectClassName}
            >
              <option value="">Seleccionar tienda...</option>
              {allVenues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
            <VenueSelectTablet
              venues={allVenues}
              selectedVenueId={venueID}
              onVenuePick={pickVenue}
            />
          </>
        )}

        {!isSuperuser && ownedShops && ownedShops.length > 1 && (
          <>
            <select
              value={venueID || ''}
              onChange={(e) => {
                const selectedVenueId = e.target.value;
                const venue = ownedShops.find(v => String(v.id) === selectedVenueId);
                pickVenue(venue);
              }}
              className={nativeSelectClassName}
            >
              <option value="">Seleccionar tienda...</option>
              {ownedShops.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
            <VenueSelectTablet
              venues={ownedShops}
              selectedVenueId={venueID}
              onVenuePick={pickVenue}
            />
          </>
        )}

        <h1 className="max-lg:portrait:hidden text-xs sm:text-sm font-bold truncate">
          <span className="hidden sm:inline">
            Estas en la tienda: {shopName}, usuario: {email ?? 'Sin email'}
            {accessPrivileges && (
              <span className="ml-1 text-purple-600 font-semibold">({roleLabel})</span>
            )}
          </span>
          <span className="inline sm:hidden">
            {shopName} | {email?.split('@')[0] ?? 'Sin email'}
            {accessPrivileges ? ` (${roleLabel})` : ''}
          </span>
        </h1>

        <div className="hidden max-lg:portrait:block min-w-0 text-left">
          <p className="text-xs sm:text-sm font-bold leading-snug text-[#0A0A0A] break-words">
            Estás en la tienda: {shopName}
          </p>
          <p className="mt-0.5 text-xs sm:text-sm leading-snug text-gray-700 break-all">
            Usuario: {email ?? 'Sin email'}
            {accessPrivileges ? (
              <span className="ml-1 font-semibold text-purple-600">({roleLabel})</span>
            ) : null}
          </p>
        </div>
      </div>

      {/* spec: entradas-control#version-nueva — solo existe cuando hay un frontend más nuevo
          publicado que el de esta pestaña; el resto del tiempo la cabecera no cambia. Va en la
          cabecera (y no en una franja debajo) porque el cajón del carrito del Dispensario y el
          asistente van anclados a `top-16` y taparían cualquier franja. NUNCA recarga sola: puede
          haber un cobro a medias. `ghost` no trae borde ni fondo, así los tokens de aviso no
          compiten con los de la variante. El que cede ancho es el título de la tienda (`truncate`). */}
      {hayVersionNueva && (
        <Button
          variant="ghost"
          size="sm"
          data-testid="aviso-version-nueva"
          aria-label="Hay una versión nueva de la aplicación: recargar"
          title="Hay una versión nueva de la aplicación"
          className="ml-2 shrink-0 min-h-[44px] border border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)]"
          disabled={recargando}
          aria-busy={recargando}
          onClick={() => {
            if (recargando) return;
            setRecargando(true);
            onRecargar();
          }}
        >
          <HiRefresh size={TAMANO_ICONO.sm} className="shrink-0 text-[var(--toast-warning-icon)]" aria-hidden />
          <span className="hidden sm:inline">{recargando ? 'Recargando…' : 'Versión nueva · Recargar'}</span>
          <span className="sm:hidden">{recargando ? 'Recargando…' : 'Recargar'}</span>
        </Button>
      )}

      <Menu as="div" className="relative max-lg:portrait:shrink-0">
        <Menu.Button aria-label="Menú de usuario" className="ml-2 max-lg:portrait:ml-0 bg-gray-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-neutral-400">
          <span className="sr-only">Open user menu</span>
          <img
            key={`avatar-${venueID}-${venueImagePath || 'default'}`}
            src={avatarUrl}
            alt={`${selectedVenue?.name || 'Venue'} avatar`}
            className="h-10 w-10 rounded-full object-cover"
            onError={(e) => {
              e.target.src = notFoundImage;
            }}
          />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          {/* z-50: por ENCIMA de las cabeceras sticky de sección (z-30 desde #652)
              y por debajo de los modales. Sin él, «Nueva caja» pintaba encima de
              este menú (reporte CRUO en tablet, 05-09-2026). */}
          <Menu.Items className="origin-top-right absolute right-0 z-50 mt-2 w-48 rounded-sm shadow-md p-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={() => navigate('/profile')}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Your Profile
                </div>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={() => navigate('/settings')}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Settings
                </div>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={handleLogout}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Sign out
                </div>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
}
